<template>
  <div 
    class="station-schedule-row schedule-row-container station-optimiser-panel-draggable station-assembly-draggable station-batch-draggable"
    @click.prevent="onClick"
    :data-type="panelType"
    :data-panelid="id"
    :data-stockid="stockId"
    :data-barcode="panelBarcode"
    :data-width="panelWidth"
    :data-height="panelHeight"
    :data-grain="grainDirection"
    :data-facesideup="stockFaceSideUpType">
    <div :class="{'schedule-top-area': true, 'schedule-row-hold': onHold || isStoppedRecut || isStoppedCancelled}">
      <div class="schedule-row-highlight-bar" :style="{'background-color': sideBarColour}">
      </div>
      <div class="schedule-row-contents">
        <div class="row no-gutters">
          <div class="col-6 station-row-truncate">
            <h1>{{stockName}}</h1>
          </div>
          <div class="col-2 station-row-truncate">
            <h1>{{panelHeight}} x {{panelWidth}}</h1>
          </div>
          <template v-if="displayPanelStationStatus">
            <div class="col-1" v-if="statusCurrent || currentStation">
              Current:
            </div>
            <div class="col-3" v-if="statusCurrent || currentStation">
              <div :class="['station-schedule-row-highlight', 'text-right', {'station-schedule-row-ready': isReady, 'station-schedule-row-scanned-in': isCurrentTaskInProduction && currentStation, 'station-schedule-row-current-task-at-station': isCurrentTaskAtStation}]">
                <template v-if="isCurrentTaskInProduction && currentStation">At {{currentStation}}</template>
                <template v-if="!isCurrentTaskInProduction || !currentStation">{{statusCurrent}}</template>
              </div>
            </div>
          </template>
          <template v-if="displayPanelOptimiserStatus">
            <div class="col-1 station-schedule-row-highlight">
              Status:
            </div>
            <div class="col-3 text-right station-schedule-row-label-area station-schedule-row-truncate">
              <LabelBadge labelClass="label-optimiser-sheet-status-open" v-if="isOptimiserStatusAvailable">AVAILABLE</LabelBadge>
              <LabelBadge labelClass="label-optimiser-sheet-status-editing" v-if="isOptimiserStatusEditing">EDITING SHEET</LabelBadge>
              <LabelBadge labelClass="label-optimiser-sheet-status-cutting" v-if="isOptimiserStatusCutting">CUTTING</LabelBadge>
            </div>
          </template>
        </div>
        <div class="row no-gutters">
          <div class="col-6 station-schedule-row-truncate">
            <i class="far fa-user-circle mr-2 fa-fw"></i><span class="station-schedule-row-highlight">{{orderCustomer}}</span>
          </div>
          <div class="col-2 station-schedule-row-truncate">
            <i :class="['fas', 'fa-clock', 'mr-2', 'fa-fw', {'text-danger': isOverdue}]" v-if="dueDate"></i>
            <span class="station-schedule-row-highlight" v-if="!isOverdue && dueDate">{{dueDate | formatTime}}</span>
            <span class="station-schedule-row-highlight station-schedule-row-highlight-overdue text-danger" v-if="isOverdue && dueDate">NOW</span>
          </div>
          <template v-if="displayPanelStationStatus">
            <div class="col-1" v-if="statusNext">
              Next:
            </div>
            <div class="col-3" v-if="statusNext">
              <div class="station-schedule-row-highlight text-right">{{statusNext}}</div>
            </div>
          </template>
        </div>
        <div class="row no-gutters">
          <div class="col-2 station-schedule-row-truncate">
            <i class="far fa-hashtag mr-2 fa-fw"></i><span class="station-schedule-row-highlight">{{panelIndex+1}} / {{panelQuantity}}</span>
          </div>
          <div class="col-2 station-schedule-row-truncate">
            {{orderSeqNum}}
          </div>
          <div class="col-2 station-schedule-row-truncate">
            {{panelBarcode}}
          </div>
          <div :class="['col-6', 'text-right', displayPanelOptimiserStatus ? 'station-schedule-row-label-area-small' : 'station-schedule-row-label-area', 'station-schedule-row-truncate']">
            <slot name="labels" />
          </div>
        </div>
      </div>
    </div>
    <div class="schedule-progress-area">
    </div>
  </div>
</template>

<style lang="scss" scoped>
.schedule-row-container {
  margin: 5px;
  height: 76px;
  display: flex;
  flex-direction: column;
  color: #A0A0A0;
  cursor: pointer;
  transition: all 0.5s;   /* For transition animations */
}
.schedule-top-area {
  background-color: #333333;
  display: flex;
  flex-direction: row;
  flex: 1.0;
}
.schedule-row-hold {
  background-image: linear-gradient(135deg, #331c1c 33.33%, #4d2121 33.33%, #4d2121 50%, #331c1c 50%, #331c1c 83.33%, #4d2121 83.33%, #4d2121 100%);
  background-size: 42.43px 42.43px;
}
.schedule-row-highlight-bar {
  height: 100%;
  width: 4px;
}
.schedule-row-contents {
  flex: 1.0;
  padding: 4px;
}
.schedule-row-contents > .row {
  margin-bottom: 2px;
}
.schedule-progress-area {
  height: 4px;
}


</style>

<script>
import moment from 'moment'

import { OptimiserSheetStatusTypes } from '@/store/types'

export default {
  name: 'StationScheduleRow',
  props: {
    id: Number,
    stockId: Number,
    stockName: String,
    panelType: String,
    panelWidth: Number,
    panelHeight: Number,
    grainDirection: String,
    stockFaceSideUpType: String,
    orderSeqNum: String,
    orderCustomer: String,
    colourHighlight: String,
    statusCurrent: String,
    statusNext: String,
    currentStation: String,
    isReady: Boolean,
    isCurrentTaskInProduction: Boolean,
    isCurrentTaskAtStation: Boolean,
    panelBarcode: String,
    panelIndex: Number,
    panelQuantity: Number,
    dueDate: String,
    onHold: Boolean,
    isStoppedRecut: Boolean,
    isStoppedCancelled: Boolean,
    optimiserSheetId: Number,
    optimiserSheetStatus: String,
    displayPanelStationStatus: Boolean,
    displayPanelOptimiserStatus: Boolean
  },
  computed: {
    sideBarColour() {
      return this.colourHighlight
    },
    isOverdue() {
      if(!this.dueDate)
        return false
      return moment(this.dueDate).isBefore(moment())
    },
    isOptimiserStatusAvailable() {
      if(!this.optimiserSheetId || this.optimiserSheetStatus == OptimiserSheetStatusTypes.open)
        return true
      
      // Processing status is now available for use by the editor
      // Sheets processed will have panels dragged into the editor removed from processing on the server
      if(!this.optimiserSheetId || this.optimiserSheetStatus == OptimiserSheetStatusTypes.processing)
        return true
      return false
    },
    isOptimiserStatusEditing() {
      if(this.optimiserSheetId && this.optimiserSheetStatus == OptimiserSheetStatusTypes.editing)
        return true

      return false
    },
    isOptimiserStatusCutting() {
      if(this.optimiserSheetId && this.optimiserSheetStatus == OptimiserSheetStatusTypes.cutting)
        return true
      return false
    },
  },
  methods: {
    onClick(evt) {
      this.$emit('click', evt)
    }
  }
}
</script>